import { EventSystem } from '@pbk-web/state';
import type { FCWithChildren } from '@pbk-web/types';
import { FormType, Roles } from '@pbk-web/types';
import { CheckIcon, ContextMenu, Loader } from '@pbk-web/ui';
import { LanguageUtils, nextTick } from '@pbk-web/utils';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useFormAction } from '../../../contexts/FormActionContext';
import { useFormRendererInfo } from '../../../contexts/FormRendererContext';
import type { FormActionWrapperProps } from '../../../contexts/FormRendererDesignContextTypes';
import { FormRendererMode } from '../../../contexts/FormRendererDesignContextTypes';
import { useFormSectionRendererInfo } from '../../../contexts/FormSectionRendererContext';
import type CommentEvent from '../../../events/QuestionCommentEvent';
import { useCurrentActivityCommentsStats, useInlineAnswerHistory } from '../../../global-state/Forms';
import usePermissions from '../../../hooks/permissions/usePermissions';
import type { DocumentResponse } from '../../../models/Document';
import FormUtils from '../../../utils/FormUtils';
import CirclePlusIcon from '../../shared/icon/CirclePlusIcon';
import CommentIcon from '../../shared/icon/CommentIcon';
import FlagIcon from '../../shared/icon/FlagIcon';
import InlineAnswerHistory from './InlineAnswerHistory';

const FormActionWrapperV2: FCWithChildren<FormActionWrapperProps> = (props) => {
  const { children } = props;

  const { isDisplayedInline, clientFormId, toHighlight, featureToggles, answersBusySaving, rootContext, placeholders, config } =
    useFormRendererInfo();
  const { mode } = useFormSectionRendererInfo();
  const { currentAction, actionValid, currentSection } = useFormAction();
  const { t } = useTranslation(['form']);
  const commentStats = useCurrentActivityCommentsStats((x) => x.value);
  const inlineAnswerHistory = useInlineAnswerHistory((x) => x.value);

  const [highlighted, setHighlighted] = useState(false);
  const selfRef = useRef<HTMLDivElement>(null);

  const commentCount = useMemo(() => commentStats.actionCommentsCounts[currentAction.id], [commentStats.actionCommentsCounts, currentAction.id]);

  const hasPermission = usePermissions();

  useEffect(() => {
    if (!toHighlight) {
      setHighlighted(false);
      return;
    }
  }, [toHighlight]);

  useEffect(() => {
    if (toHighlight) {
      const isHighlighted = toHighlight.formId === clientFormId && currentAction.id === toHighlight.sourceId;
      setHighlighted(isHighlighted);
      if (isHighlighted && commentCount > 0 && !toHighlight.isRisk) {
        EventSystem.fireEvent('question-comment-open', {
          sourceId: currentAction.id,
          sectionId: currentSection.id,
        });
      }
    }
  }, [clientFormId, commentCount, currentAction.id, currentSection.id, toHighlight]);

  useEffect(() => {
    if (highlighted) {
      nextTick(() => selfRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'end' }));
    }
  }, [highlighted]);

  const shouldDisplayActionsButton = useMemo(() => !isDisplayedInline && config.type !== FormType.Quiz, [config.type, isDisplayedInline]);

  useEffect(() => {
    const handleCommentOpen = (event: CommentEvent) => {
      setHighlighted(currentAction.id === event.sourceId);
    };

    const handleCommentCancel = () => {
      setHighlighted(false);
    };

    EventSystem.listen('question-comment-open', handleCommentOpen);
    EventSystem.listen('comment-cancel', handleCommentCancel);
    EventSystem.listen('question-comment-new', handleCommentOpen);

    return () => {
      EventSystem.stopListening('question-comment-open', handleCommentOpen);
      EventSystem.stopListening('comment-cancel', handleCommentCancel);
      EventSystem.stopListening('question-comment-new', handleCommentOpen);
    };
  }, [currentAction.id]);

  return (
    <div
      data-testid="action-wrapper"
      className={`mb-2 transition-colors ${highlighted ? 'bg-background-mid-1' : ''} group flex rounded-lg ${
        !currentAction.noninteractive && !featureToggles?.disableQuestionCommentIcon && !rootContext?.clientForm?.originId && !highlighted
          ? 'hover:bg-gray-100 [&:has(:focus)]:bg-gray-100'
          : ''
      }`}
      ref={selfRef}
    >
      <div className="flex grow rounded p-4">
        {mode === FormRendererMode.EditView && (
          <div className="w-7 shrink-0">
            {!currentAction.noninteractive &&
              (answersBusySaving[currentAction.id] ? (
                <Loader size={6} centered={false} />
              ) : (
                <CheckIcon className={`text-white ${actionValid ? 'bg-semantic-success/100' : 'bg-greyscale-15/100'} h-5 w-5 rounded-full p-1`} />
              ))}
          </div>
        )}
        <div className="w-full grow">
          {children}

          {inlineAnswerHistory && <InlineAnswerHistory />}
        </div>
      </div>
      {shouldDisplayActionsButton && (
        <div className="flex w-14 items-center justify-end px-2">
          {!currentAction.noninteractive &&
            !featureToggles?.disableQuestionCommentIcon &&
            /* Start a new thread for the Question */
            !rootContext?.clientForm?.originId && (
              <ContextMenu
                items={[
                  {
                    title: t('action-menu.comment'),
                    icon: <CommentIcon className="h-4 w-4" />,
                    onClick: () => {
                      EventSystem.fireEvent('question-comment-new', {
                        sourceId: currentAction.id,
                        sectionId: currentSection.id,
                      });
                    },
                  },
                  {
                    title: t('action-menu.risk'),
                    icon: <FlagIcon className="h-5 w-5" />,
                    onClick: () =>
                      EventSystem.fireEvent('question-risk-new', {
                        action: currentAction,
                        clientFormId: rootContext?.clientFormId || '',
                        clientFormTitle:
                          rootContext?.clientForm?.type === FormType.Document
                            ? FormUtils.getDocumentTitle(rootContext?.clientForm as DocumentResponse)
                            : rootContext?.clientForm?.subtitle || LanguageUtils.getTranslation('title', rootContext?.config.translations || {}),
                        placeholders: placeholders,
                      }),
                    hide: !hasPermission([Roles.Employee, Roles.ExternalContributor]) && hasPermission(Roles.ExternalAuditor),
                  },
                ]}
                type="icon"
              >
                {(ref, isOpen) => (
                  <div ref={ref}>
                    <CirclePlusIcon
                      className={`${isOpen ? 'visible' : 'invisible'} text-background-mid-1 h-8 w-8 shrink-0 cursor-pointer group-hover:visible group-[&:has(:focus)]:visible`}
                    />
                  </div>
                )}
              </ContextMenu>
            )}
        </div>
      )}
    </div>
  );
};

export default FormActionWrapperV2;
